import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircleIcon, CheckIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LoadingWheel from "../shared/LoadingWheel";
import useVehiclePricingMatrix from "../../data/useVehiclePricingMatrix";
import PriceFormat from "../../utils/priceFormat";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import classNames from "../../utils/classNames";
import useVehicleSingle from "../../data/useVehicleSingle";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useSettingSet from "../../data/useSettingSet";
import useCategories from "../../data/useCategories";
import useRsAutomotive from "../../data/useRsAutomotive";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import useWilmaDeletedUsers from "../../data/useWilmaDeletedUsers";
import formatDateTime from "../../utils/formatDateTime";
import { DateTime } from "luxon";
import Tooltip from "../shared/Tooltip";
import { useAcceptPartProTradeBids, usePartProTradeCoreVehicleBids } from "../../data/usePartProTradeBids";
import { IAcceptPartProTradeBid, IPartProTradeBid } from "../../models/partPro/IPartProTradeBids";
import {
  BidPicks,
  bidderName,
  groupPendingMatrixBids,
  livePicks,
  matrixCellKey,
  picksToAccept,
  toggleBidPick,
} from "../../utils/pricingMatrixBids";
import { useAuth } from "../../contexts/AuthContext";
import useNotification from "../notifications/useNotifications";
import ConfirmModal from "../shared/Confirm/ConfirmModal";

const normalizeRsKey = (value: string | null | undefined) =>
  (value ?? "").trim().toLowerCase();

const parseRsPrice = (value: string | null | undefined) =>
  parseFloat((value ?? "").replace("£", ""));

// Recycling accounts are sourcing/flow destinations, not biddable — they
// shouldn't appear as columns in the pricing matrix.
const RECYCLING_ACCOUNT_TYPE = 8;

const accountTypeLabelMap: Record<number, string> = {
  5: "Reuse",
  6: "Remanufacturing",
  7: "Reintegration",
  8: "Recycling",
};

const accountTypeLabelClassMap: Record<number, string> = {
  5: "text-emerald-700",
  6: "text-orange-700",
  7: "text-blue-700",
  8: "text-purple-700",
};

interface VehiclePricingMatrixProps {
  vehicleId?: string;
}

export default function VehiclePricingMatrix({ vehicleId }: VehiclePricingMatrixProps) {
  const { t } = useTranslation("vehicles");
  const { vehiclePricingMatrix, vehicleKtypePricing, updateVehiclePricingMatrix } = useVehiclePricingMatrix(vehicleId, "VehiclePricingMatrix");
  const { saveHandlers } = useDefaultCRUDHandlers(t("Vehicle Pricing Matrix"));
  const { patchVehicle } = useVehiclesMutations();
  const { saveHandlers: biddingHandlers } = useDefaultCRUDHandlers(t("Bidding / Sourcing"));
  const [priceInputs, setPriceInputs] = useState<Record<string, string>>({});
  const accounts = (vehiclePricingMatrix.data?.data?.accounts ?? []).filter(
    (account) => account.accountType !== RECYCLING_ACCOUNT_TYPE
  );
  const categories = vehiclePricingMatrix.data?.data?.categories ?? [];

  const { wilmaUsers } = useWilmaDeletedUsers();
  const getUserName = useCallback(
    (userId: string | null) => {
      if (!userId) return undefined;
      const user = wilmaUsers.data?.data?.find((u: any) => u.id === userId);
      return user ? `${user.forename} ${user.surname}` : undefined;
    },
    [wilmaUsers.data]
  );

  // --- RS Automotive pricing (lives outside the matrix endpoint) ---
  const { vehicle: singleVehicle } = useVehicleSingle(vehicleId);
  const { isSet: hasRsKey } = useSettingSet("Rs:ApiKey");
  const { categories: allCategories } = useCategories();
  const { dataByKtype, dataByVrm, dataByVin } = useRsAutomotive();
  const hasTmeFeature = CheckFeatureFlag("TME");

  // --- PartPro Trade bids still waiting for a decision (read live, never saved to the matrix) ---
  const tradeBids = usePartProTradeCoreVehicleBids(vehicleId);
  const tradeVehicle = tradeBids.data?.data ?? null;
  const acceptBids = useAcceptPartProTradeBids(vehicleId);
  const { addNotification } = useNotification();
  const { claims } = useAuth();
  // Accepting is admin only - awards are final and email the buyers
  const isAdmin = !!claims?.isAdmin;
  const [picks, setPicks] = useState<BidPicks>({});
  const [confirmAccept, setConfirmAccept] = useState(false);

  const [rsPriceByCategoryId, setRsPriceByCategoryId] = useState<Record<string, number>>({});
  const [rsLoading, setRsLoading] = useState(false);

  // RS products are keyed by the category's RsName, which the matrix endpoint
  // doesn't return — map it in from the full category list.
  const rsNameByCategoryId = useMemo(() => {
    const map = new Map<string, string>();
    allCategories.data?.data?.forEach((c) => {
      if (c.rsName) map.set(c.id, c.rsName);
    });
    return map;
  }, [allCategories.data]);

  const applyRsProducts = useCallback((data: any) => {
    const products = data?.rs_products ?? {};
    const priceByRsName = new Map<string, number>();

    Object.entries(products).forEach(([key, value]) => {
      const price = parseRsPrice((value as { rs_price?: string })?.rs_price);
      if (Number.isNaN(price)) return;
      priceByRsName.set(normalizeRsKey(key), price);
    });

    const next: Record<string, number> = {};
    rsNameByCategoryId.forEach((rsName, categoryId) => {
      const price = priceByRsName.get(normalizeRsKey(rsName));
      if (price != null) next[categoryId] = price;
    });

    setRsPriceByCategoryId(next);
  }, [rsNameByCategoryId]);

  const runRsLookup = useCallback(async () => {
    const v = singleVehicle.data?.data;
    if (!v || !hasRsKey) return;

    const ktype = v.kType;
    const vrm = v.vrm;
    const vin = v.combinedVIN;

    const byKtype = async () =>
      ktype && vrm ? await dataByKtype.mutateAsync({ ktype, reg_no: vrm }) : null;
    const byVrm = async () =>
      vrm ? await dataByVrm.mutateAsync({ reg_no: vrm }) : null;
    const byVin = async () =>
      vin ? await dataByVin.mutateAsync({ vin }) : null;

    // TME customers get better data from the VRM, so that goes first with the VIN
    // as its backup and KType last. Everyone else keeps KType first.
    const lookups = hasTmeFeature ? [byVrm, byVin, byKtype] : [byKtype, byVrm, byVin];

    setRsLoading(true);
    try {
      for (const lookup of lookups) {
        const res: any = await lookup();
        if (res?.rs_status === "success") {
          applyRsProducts(res.rs_data);
          return;
        }
      }
    } catch (error) {
      console.error("RS pricing matrix lookup failed:", error);
    } finally {
      setRsLoading(false);
    }
  }, [singleVehicle.data, hasRsKey, hasTmeFeature, dataByKtype, dataByVrm, dataByVin, applyRsProducts]);

  // Auto-run the lookup once per vehicle when everything's ready.
  const rsLookupVehicleRef = useRef<string | null>(null);
  useEffect(() => {
    if (!hasRsKey || !singleVehicle.data?.data || rsNameByCategoryId.size === 0) return;
    if (rsLookupVehicleRef.current === (vehicleId ?? null)) return;

    rsLookupVehicleRef.current = vehicleId ?? null;
    runRsLookup();
  }, [hasRsKey, singleVehicle.data, rsNameByCategoryId, vehicleId, runRsLookup]);

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    allCategories.data?.data?.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [allCategories.data]);

  // Matrix rows come from account-linked categories only. RS can return prices
  // for categories that aren't linked to any account, so surface those as
  // extra rows (with no account cells).
  const displayCategories = useMemo(() => {
    const existingIds = new Set(categories.map((c) => c.partCategoryId));
    const extras: typeof categories = Object.keys(rsPriceByCategoryId)
      .filter((id) => !existingIds.has(id))
      .map((id) => ({
        partCategoryId: id,
        partCategoryName: categoryNameById.get(id) ?? t("Unknown category"),
        cells: [],
      }));

    return [...categories, ...extras];
  }, [categories, rsPriceByCategoryId, categoryNameById, t]);

  const cellDataByKey = useMemo(() => {
    const data: Record<
      string,
      { enabled: boolean; price: number | null; pricedById: string | null; pricedAt: string | null; fromPartProTrade: boolean }
    > = {};

    categories.forEach((category) => {
      category.cells.forEach((cell) => {
        const key = `${category.partCategoryId}-${cell.accountId}`;
        data[key] = {
          enabled: cell.enabled,
          price: cell.price,
          pricedById: cell.pricedById,
          pricedAt: cell.pricedAt,
          fromPartProTrade: cell.fromPartProTrade,
        };
      });
    });

    return data;
  }, [categories]);

  // Most recent price per account/category from other vehicles with the same
  // KType — surfaced as a one-click "apply" button beside each price input.
  const ktypePriceByKey = useMemo(() => {
    const map: Record<string, number> = {};
    vehicleKtypePricing.data?.data?.cells?.forEach((cell) => {
      if (cell.price == null) return;
      map[`${cell.partCategoryId}-${cell.accountId}`] = cell.price;
    });
    return map;
  }, [vehicleKtypePricing.data]);

  useEffect(() => {
    const nextInputs: Record<string, string> = {};

    categories.forEach((category) => {
      category.cells.forEach((cell) => {
        const key = `${category.partCategoryId}-${cell.accountId}`;
        nextInputs[key] = cell.price != null ? String(cell.price) : "";
      });
    });

    setPriceInputs(nextInputs);
  }, [categories]);

  if (vehiclePricingMatrix.isLoading) {
    return (
      <div className="p-4 bg-white rounded shadow min-h-[220px] flex items-center justify-center">
        <LoadingWheel />
      </div>
    );
  }

  if (vehiclePricingMatrix.isError) {
    return (
      <div className="p-4 bg-white rounded shadow text-red-600 font-medium">
        {t("Unable to load pricing matrix.")}
      </div>
    );
  }

  if (!accounts.length) {
    return (
      <div className="p-4 bg-white rounded shadow text-sm text-gray-600">
        {t("No reuse accounts are configured.")}
      </div>
    );
  }

  if (!displayCategories.length) {
    return (
      <div className="p-4 bg-white rounded shadow text-sm text-gray-600">
        {t("No vehicle part categories found for pricing.")}
      </div>
    );
  }

  const handleCellBlur = (partCategoryId: string, accountId: string) => {
    const key = `${partCategoryId}-${accountId}`;
    const cellData = cellDataByKey[key];
    if (!cellData?.enabled) return;

    const value = priceInputs[key]?.trim() ?? "";
    const parsed = value === "" ? null : Number(value);

    if (value !== "" && Number.isNaN(parsed)) {
      setPriceInputs((prev) => ({
        ...prev,
        [key]: cellData.price != null ? String(cellData.price) : "",
      }));
      return;
    }

    if (parsed === cellData.price) return;

    updateVehiclePricingMatrix.mutate(
      {
        prices: [{ accountId, partCategoryId, price: parsed }],
      },
      saveHandlers
    );
  };

  const handleApplyKtypePrice = (partCategoryId: string, accountId: string) => {
    const key = `${partCategoryId}-${accountId}`;
    const cellData = cellDataByKey[key];
    if (!cellData?.enabled) return;

    const price = ktypePriceByKey[key];
    if (price == null || price === cellData.price) return;

    setPriceInputs((prev) => ({ ...prev, [key]: String(price) }));

    updateVehiclePricingMatrix.mutate(
      {
        prices: [{ accountId, partCategoryId, price }],
      },
      saveHandlers
    );
  };

  const getBestPrice = (partCategoryId: string) => {
    const values = accounts
      .map((account) => {
        const key = `${partCategoryId}-${account.accountId}`;
        const cellData = cellDataByKey[key];
        if (!cellData?.enabled) return null;

        const value = priceInputs[key]?.trim() ?? "";
        if (!value) return null;

        const parsed = Number(value);
        return Number.isNaN(parsed) ? null : parsed;
      })
      .filter((value): value is number => value != null);

    const rsPrice = rsPriceByCategoryId[partCategoryId];
    if (rsPrice != null) values.push(rsPrice);

    if (values.length === 0) return null;
    return Math.max(...values);
  };

  const totalBest = displayCategories.reduce((sum, category) => {
    const bestPrice = getBestPrice(category.partCategoryId);
    return sum + (bestPrice ?? 0);
  }, 0);

  const biddingComplete = !!singleVehicle.data?.data?.biddingComplete;

  // Only the cells on screen - Recycling columns are hidden, so their bids go on the row instead
  const visibleCellKeys = new Set<string>();
  categories.forEach((category) =>
    accounts.forEach((account) => {
      const key = matrixCellKey(category.partCategoryId, account.accountId);
      if (cellDataByKey[key]?.enabled) visibleCellKeys.add(key);
    })
  );
  const pendingBids = groupPendingMatrixBids(tradeVehicle, visibleCellKeys);

  const tradeSummary = (() => {
    if (!tradeVehicle) return null;
    const counts = { bids: pendingBids.bidCount, categories: pendingBids.categoryCount };
    const endsAt = tradeVehicle.biddingEndsAt
      ? ` ${t("Bidding ends {{date}}.", { date: formatDateTime(tradeVehicle.biddingEndsAt, DateTime.DATETIME_MED) })}`
      : "";

    switch (tradeVehicle.status) {
      case "OPEN":
        return pendingBids.bidCount > 0
          ? t("PartPro Trade: {{bids}} bids on {{categories}} categories, none accepted yet.", counts) + endsAt
          : t("On PartPro Trade, no bids yet.") + endsAt;
      case "CLOSED":
        return t("PartPro Trade bidding has ended: {{bids}} bids on {{categories}} categories are waiting for a decision.", counts);
      case "COMPLETED":
        return t("PartPro Trade bidding is complete.");
      default:
        return null;
    }
  })();

  // While bidding is open, clicking picks a bid to accept on "Mark quoting complete"; once it
  // has ended, clicking accepts straight away.
  const canPick = isAdmin && tradeVehicle?.status === "OPEN";
  const canAcceptNow = isAdmin && tradeVehicle?.status === "CLOSED";
  const activePicks = livePicks(picks, pendingBids);
  const pickedBids = (tradeVehicle?.lots ?? [])
    .filter((lot) => activePicks[lot.id])
    .map((lot) => ({ lot, bid: lot.bids.find((b) => b.id === activePicks[lot.id])! }));

  const bidHint = (bid: IPartProTradeBid) => {
    if (canPick) {
      return activePicks[pendingBids.lotIdByBidId[bid.id]] === bid.id
        ? t("Picked - accepted when you mark quoting complete. Click to un-pick.")
        : t("Click to pick this bid");
    }
    return canAcceptNow ? t("Click to accept this bid") : t("Not accepted yet");
  };

  const bidTooltip = (bid: IPartProTradeBid) => (
    <div className="space-y-1 text-left">
      <div className="font-medium">
        {t("{{amount}} from {{name}}", { amount: PriceFormat(bid.amount), name: bidderName(bid) })}
        {bid.isAutoPick && ` · ${t("PartPro's pick")}`}
      </div>
      <div className="text-gray-300">{formatDateTime(bid.amountSetAt, DateTime.DATETIME_MED)}</div>
      {bid.message && <div className="italic text-gray-300">"{bid.message}"</div>}
      <div className="text-amber-300">{bidHint(bid)}</div>
    </div>
  );

  const accept = (bids: IAcceptPartProTradeBid[], onDone?: () => void) => {
    if (!tradeVehicle) return;

    acceptBids.mutate(
      { tradeVehicleId: tradeVehicle.id, bids },
      {
        onSuccess: (res) => {
          if (res.message) addNotification({ variant: "success", primaryText: res.message });
          onDone?.();
        },
        onError: (err: unknown) => {
          setConfirmAccept(false);
          addNotification({
            variant: "error",
            primaryText: t("PartPro Trade"),
            secondaryText: err instanceof Error ? err.message : t("Something went wrong"),
          });
        },
      }
    );
  };

  const handleBidClick = (bid: IPartProTradeBid) => {
    const lotId = pendingBids.lotIdByBidId[bid.id];
    if (!lotId) return;

    if (canPick) setPicks(toggleBidPick(activePicks, lotId, bid.id));
    else if (canAcceptNow) accept([{ lotId, bidId: bid.id }]);
  };

  const renderBid = (bid: IPartProTradeBid, label: string) => {
    const picked = activePicks[pendingBids.lotIdByBidId[bid.id]] === bid.id;
    const clickable = canPick || canAcceptNow;

    return (
      <Tooltip key={bid.id} content={bidTooltip(bid)}>
        <button
          type="button"
          onClick={() => handleBidClick(bid)}
          disabled={!clickable || acceptBids.isLoading}
          className={classNames(
            "inline-flex items-center gap-1 whitespace-nowrap rounded border px-2 py-1 text-xs font-medium transition-colors",
            picked
              ? "border-amber-600 bg-amber-500 text-white"
              : "border-amber-300 bg-amber-50 text-amber-800",
            clickable && !picked && "hover:bg-amber-100",
            clickable ? "disabled:opacity-60" : "cursor-default"
          )}
        >
          {picked && <CheckIcon className="h-3 w-3" />}
          {label}
        </button>
      </Tooltip>
    );
  };

  const markBiddingComplete = (complete: boolean) => {
    if (!vehicleId) return;

    patchVehicle.mutate(
      { vehicleId, patch: { biddingComplete: complete } },
      biddingHandlers
    );
  };

  const handleToggleBidding = () => {
    // Picked bids are accepted first, behind a confirm - awards are final
    if (!biddingComplete && pickedBids.length > 0) {
      setConfirmAccept(true);
      return;
    }

    markBiddingComplete(!biddingComplete);
  };

  const handleConfirmAccept = () =>
    accept(picksToAccept(activePicks), () => {
      setPicks({});
      setConfirmAccept(false);
      markBiddingComplete(true);
    });

  return (
    <div className="space-y-4">
      <div className="bg-white rounded shadow p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{t("Bidding / Sourcing")}</h2>
          <p className="text-sm text-gray-500">{t("Mark once bidding and sourcing is finished.")}</p>
          {tradeSummary && tradeVehicle && (
            <p className="mt-1 text-sm text-amber-700">
              {tradeSummary}{" "}
              <Link
                to={`/bids/${tradeVehicle.id}`}
                className="font-medium text-primary-600 underline hover:text-primary-700"
              >
                {t("View bids")}
              </Link>
            </p>
          )}
          {pendingBids.bidCount > 0 && (
            <p className="mt-1 text-sm text-gray-600">
              {canPick
                ? pickedBids.length > 0
                  ? t("{{picked}} picked. Mark quoting complete to end bidding and accept them.", { picked: pickedBids.length })
                  : t("Click bids to pick the winners, then mark quoting complete to end bidding and accept them.")
                : canAcceptNow
                  ? t("Click a bid to accept it into the matrix.")
                  : t("Only an admin can accept bids.")}
            </p>
          )}
          {tradeBids.isError && (
            <p className="mt-1 text-sm text-gray-500">{t("PartPro bids could not be loaded.")}</p>
          )}
        </div>
        <button
          type="button"
          onClick={handleToggleBidding}
          disabled={patchVehicle.isLoading || acceptBids.isLoading}
          className={classNames(
            "inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors disabled:opacity-60",
            biddingComplete
              ? "bg-green-600 hover:bg-green-700"
              : "bg-amber-500 hover:bg-amber-600"
          )}
        >
          <CheckCircleIcon className="h-5 w-5" />
          {biddingComplete ? t("Quoting complete") : t("Mark quoting complete")}
        </button>
      </div>

      <div className="bg-white rounded shadow p-4">
        <table className="min-w-full border border-gray-200 rounded-md">
          {/* Header sticks to the page under the h-16 Topbar. Deliberately no
              overflow wrapper: any scroll container here would capture the
              sticky positioning and the row would stop following the page. */}
          <thead className="bg-gray-100 [&_th]:sticky [&_th]:top-16 [&_th]:z-10 [&_th]:bg-gray-100 [&_th]:border-gray-200 [&_th]:border-b [&_th]:border-r [&_th]:shadow-[inset_0_-1px_0_0_#e5e7eb]">
            <tr>
              <th className="text-left px-3 py-2 text-sm font-semibold text-gray-800">
                {t("Part Category")}
              </th>
              {accounts.map((account) => (
                <th
                  key={account.accountId}
                  className="text-center px-3 py-2 text-sm font-semibold text-gray-800"
                >
                  <div className="flex flex-col">
                    <span>{account.accountName}</span>
                    <span
                      className={classNames(
                        "text-xs font-normal",
                        accountTypeLabelClassMap[account.accountType] ?? "text-gray-500"
                      )}
                    >
                      {accountTypeLabelMap[account.accountType] ? t(accountTypeLabelMap[account.accountType]) : t("Type {{type}}", { type: account.accountType })}
                    </span>
                  </div>
                </th>
              ))}
              {hasRsKey && (
                <th className="text-center px-3 py-2 text-sm font-semibold text-gray-800 min-w-[80px]">
                  <div className="flex flex-col">
                    <span>RS</span>
                    <span className="text-xs font-normal text-[#69141E]">{t("RS Automotive")}</span>
                  </div>
                </th>
              )}
              <th className="text-center px-3 py-2 text-sm font-semibold text-gray-800 min-w-[60px]">
                {t("Best")}
              </th>
            </tr>
          </thead>
          <tbody>
            {displayCategories.map((category) => {
              const bestPrice = getBestPrice(category.partCategoryId);

              return (
                <tr key={category.partCategoryId} className="border-b [&_td]:border-r border-gray-200">
                  <td className="px-3 py-2 text-sm text-gray-800 font-medium">
                    {category.partCategoryName}
                    {pendingBids.otherByCategory[category.partCategoryId] && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {pendingBids.otherByCategory[category.partCategoryId].map((bid) =>
                          renderBid(bid, t("Bid {{amount}} from {{name}}", { amount: PriceFormat(bid.amount), name: bidderName(bid) }))
                        )}
                      </div>
                    )}
                  </td>
                  {accounts.map((account) => {
                    const key = `${category.partCategoryId}-${account.accountId}`;
                    const cellData = cellDataByKey[key];
                    const disabled = !cellData?.enabled;

                    const pricedByName = getUserName(cellData?.pricedById ?? null);
                    const pricedTooltip =
                      cellData?.pricedAt != null
                        ? t(
                            cellData.fromPartProTrade
                              ? "PartPro Trade bid accepted by {{name}} on {{date}}"
                              : "Priced by {{name}} on {{date}}",
                            {
                              name: pricedByName ?? t("Unknown"),
                              date: formatDateTime(cellData.pricedAt, DateTime.DATETIME_MED),
                            }
                          )
                        : undefined;

                    return (
                      <td
                        key={key}
                        className={`px-3 py-2 text-center text-sm ${disabled ? "bg-gray-100 text-gray-400" : "text-gray-800"}`}
                      >
                        {disabled ? (
                          ""
                        ) : (
                          (() => {
                            const input = (
                              <input
                                type="number"
                                step="0.01"
                                value={priceInputs[key] ?? ""}
                                onChange={(e) =>
                                  setPriceInputs((prev) => ({
                                    ...prev,
                                    [key]: e.target.value,
                                  }))
                                }
                                onBlur={() => handleCellBlur(category.partCategoryId, account.accountId)}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-right text-sm focus:border-primary-500 focus:ring-primary-500"
                                placeholder="0.00"
                              />
                            );

                            const ktypePrice = ktypePriceByKey[key];
                            const cellBids = pendingBids.byCell[key];

                            return (
                              <div className="flex items-center gap-1">
                                {cellBids?.map((bid) =>
                                  renderBid(bid, t("Bid {{amount}}", { amount: PriceFormat(bid.amount) }))
                                )}
                                {cellData.fromPartProTrade && (
                                  <Tooltip content={t("An accepted PartPro Trade bid. Typing a new price replaces it.")}>
                                    <span className="whitespace-nowrap rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
                                      {t("Trade")}
                                    </span>
                                  </Tooltip>
                                )}
                                {ktypePrice != null && (
                                  <Tooltip content={t("Most recent price for matching vehicle")}>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleApplyKtypePrice(category.partCategoryId, account.accountId)
                                      }
                                      disabled={updateVehiclePricingMatrix.isLoading}
                                      className="whitespace-nowrap rounded border border-primary-200 bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-100 disabled:opacity-60"
                                    >
                                      {PriceFormat(ktypePrice)}
                                    </button>
                                  </Tooltip>
                                )}
                                {pricedTooltip ? (
                                  <Tooltip content={pricedTooltip} className="w-full">
                                    {input}
                                  </Tooltip>
                                ) : (
                                  input
                                )}
                              </div>
                            );
                          })()
                        )}
                      </td>
                    );
                  })}
                  {hasRsKey && (
                    <td className="px-3 py-2 text-center text-sm text-gray-800">
                      {rsLoading
                        ? "…"
                        : rsPriceByCategoryId[category.partCategoryId] != null
                          ? PriceFormat(rsPriceByCategoryId[category.partCategoryId])
                          : "—"}
                    </td>
                  )}
                  <td className="px-3 py-2 text-center text-sm font-semibold text-primary-700">
                    {bestPrice != null ? PriceFormat(bestPrice) : "—"}
                  </td>
                </tr>
              );
            })}
            <tr className="bg-gray-50">
              <td className="px-3 py-2 text-sm font-semibold text-gray-900">
                {t("Total Best")}
              </td>
              {accounts.map((account) => (
                <td key={`total-${account.accountId}`} className="px-3 py-2" />
              ))}
              {hasRsKey && <td className="px-3 py-2" />}
              <td className="px-3 py-2 text-center text-sm font-semibold text-primary-700">
                {PriceFormat(totalBest)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={confirmAccept}
        setOpen={(open: boolean) => !open && setConfirmAccept(false)}
        title={tradeVehicle?.status === "OPEN" ? t("End bidding and accept these bids?") : t("Accept these bids?")}
        message={t(
          "The winning buyers are emailed to arrange collection and payment, and everyone else on those categories is told they were unsuccessful. The amounts go into the pricing matrix. This can't be undone."
        )}
        confirmButtonText={tradeVehicle?.status === "OPEN" ? t("End bidding and accept") : t("Accept bids")}
        onConfirm={handleConfirmAccept}
        isLoading={acceptBids.isLoading}
      >
        <ul className="mt-3 space-y-1 text-sm text-gray-700">
          {pickedBids.map(({ lot, bid }) => (
            <li key={lot.id}>
              <span className="font-medium">{lot.categoryName}</span>
              {" · "}
              {t("{{amount}} from {{name}}", { amount: PriceFormat(bid.amount), name: bidderName(bid) })}
            </li>
          ))}
        </ul>
        {tradeVehicle?.status === "OPEN" && pendingBids.categoryCount > pickedBids.length && (
          <p className="mt-3 text-sm text-gray-500">
            {t("Categories you haven't picked stay waiting for a decision on the Bids page.")}
          </p>
        )}
      </ConfirmModal>
    </div>
  );
}
